import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CloudProviderMark from "../Common/CloudProviderMark";
import { getAccountCloudType } from "../../CloudProviderContext";
import { shouldFilterUsername } from "../../helpers/util_helper";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import {
  ACCOUNT_DETAIL_FONT_SX,
} from "./accountDetailTypography";
import { EditAccountTablePagination } from "./editAccountTableControls";
import {
  AccountDetailTooltip,
  TruncatedTooltipText,
} from "./editAccountTooltipStyles";

const ALLOWED_ROLES = ["Administrator"];
const ACTIVE_COLOR = "#16a34a";
const LIMITED_COLOR = "#2563eb";
const EXPIRED_COLOR = "#7a0709";
const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};
const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.25,
  px: 1,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  textAlign: "center",
  whiteSpace: "nowrap",
};
const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.25,
  px: 1,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  maxWidth: 0,
};
const ACTION_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  padding: 0,
  "&:hover": {
    backgroundColor: "#f1f5f9",
    color: "#334155",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
    borderColor: "#f1f5f9",
    backgroundColor: "#f8fafc",
  },
};
const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  "&.Mui-disabled": {
    color: "#fca5a5",
    borderColor: "#fee2e2",
    backgroundColor: "#fff",
  },
};

const formatDisplayDate = (dateInput) => {
  if (!dateInput || dateInput === "null") return "—";
  if (typeof dateInput === "object" && dateInput.date) {
    const { year, month, day } = dateInput.date;
    const dt = new Date(year, month - 1, day);
    if (isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const dt = new Date(dateInput);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getDateValue = (dateInput) => {
  if (!dateInput || dateInput === "null") return 0;
  if (typeof dateInput === "object" && dateInput.date) {
    const { year, month, day } = dateInput.date;
    return new Date(year, month - 1, day).getTime() || 0;
  }
  const dt = new Date(dateInput);
  return isNaN(dt.getTime()) ? 0 : dt.getTime();
};

export const getAccountDisplayStatus = (acct) => {
  if (acct.subscription_status === "expired") {
    return {
      key: "expired",
      label: "Expired",
      color: EXPIRED_COLOR,
      backgroundColor: "#fef2f2",
    };
  }
  if (acct.status === "INACTIVE") {
    return {
      key: "inactive",
      label: "Inactive",
      color: "#475569",
      backgroundColor: "#f1f5f9",
    };
  }
  const isAzureAccount = getAccountCloudType(acct) === "AZURE";
  if (!isAzureAccount) {
    const addonsLimited =
      acct.subscription?.addons === null ||
      acct.subscription?.addons?.length < 3 ||
      (acct.subscription?.addons || []).filter((addon) => addon.enabled).length <
        3;
    if (acct.status === "ACTIVE" && addonsLimited) {
      return {
        key: "limited",
        label: "Active",
        color: LIMITED_COLOR,
        backgroundColor: "#eff6ff",
      };
    }
  }
  return {
    key: "active",
    label: "Active",
    color: ACTIVE_COLOR,
    backgroundColor: "#ecfdf5",
  };
};

/** Display names for the three status modules (API name → UI label). */
const STATUS_MODULE_LABELS = [
  { apiName: "Event Insight", label: "Cloud Event" },
  { apiName: "Cost Insight", label: "FinOps" },
  { apiName: "Cost Optimization", label: "Cost Optimization" },
];

/** Inactive / missing modules for the blue Active (limited) status tooltip. */
export const getInactiveStatusModules = (acct) => {
  const addons = acct?.subscription?.addons;
  if (!Array.isArray(addons) || addons.length === 0) {
    return STATUS_MODULE_LABELS.map((m) => m.label);
  }
  return STATUS_MODULE_LABELS.filter(({ apiName }) => {
    const match = addons.find((addon) => addon?.name === apiName);
    return !match || !match.enabled;
  }).map((m) => m.label);
};

const getUserCount = (acct) =>
  (acct.listusr || []).filter(
    (user) =>
      user.isActivated &&
      !user.email.includes(process.env.REACT_APP_EMAIL_DOMAIN) &&
      !shouldFilterUsername(user.userName)
  ).length;

const getAddonCount = (acct) =>
  (acct.complianceAddOns || []).filter((addOn) => addOn.enabled).length +
  (acct.subscription?.addons || []).filter((addOn) => addOn.enabled).length;

const getResourceGroupName = (acct) =>
  acct.accountResourceGroups?.[0]?.resourcename || "Default";

const toTrimmedString = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const PayerCell = ({ payerId, payerName }) => {
  if (!payerId) {
    return payerName ? (
      <TruncatedTooltipText title={payerName} sx={CELL_TEXT}>
        {payerName}
      </TruncatedTooltipText>
    ) : (
      <Typography sx={{ ...CELL_TEXT, color: "#94a3b8" }}>—</Typography>
    );
  }
  return (
    <TruncatedTooltipText
      title={payerName}
      sx={{ ...CELL_TEXT, fontVariantNumeric: "tabular-nums" }}
    >
      {payerId}
    </TruncatedTooltipText>
  );
};

const STATUS_LEGEND_ITEMS = [
  {
    color: ACTIVE_COLOR,
    title: "Active Account",
    text: "All modules (Cloud Event, FinOps, Cost Optimization) enabled",
  },
  {
    color: "#dc2626",
    title: "Inactive Account",
    textBeforeIcon:
      "Account inactive due to incorrect External ID or cross-account role missing. Please click on the",
    showLockIcon: true,
    textAfterIcon: "and follow the account pairing guide.",
  },
  {
    color: LIMITED_COLOR,
    title: "Active Account",
    text: "One or more modules (Cloud Event, FinOps, Cost Optimization) disabled",
  },
  {
    color: EXPIRED_COLOR,
    title: "CloudCatcher Subscription Expired",
    text: "Please renew your subscription to enable features.",
  },
];

const AZURE_STATUS_LEGEND_ITEMS = [
  {
    color: ACTIVE_COLOR,
    title: "Active Subscription",
    text: "All Modules (CSPM, Compliance)",
  },
  {
    color: "#dc2626",
    title: "Inactive Subscription",
    text: "Subscription inactive due to deletion of service provider offer",
  },
  {
    color: EXPIRED_COLOR,
    title: "CloudCatcher Subscription Expired",
    text: "Please renew your subscription to enable features",
  },
];

const StatusLegend = ({ isAzureView = false }) => {
  const legendItems = isAzureView ? AZURE_STATUS_LEGEND_ITEMS : STATUS_LEGEND_ITEMS;

  return (
  <Box
    sx={{
      mt: 2,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
      width: "100%",
      minWidth: 0,
    }}
  >
    {legendItems.map((item) => (
      <Box
        key={`${item.title}-${item.text || item.textBeforeIcon}`}
        sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}
      >
        <FiberManualRecordIcon
          sx={{ color: item.color, fontSize: 12, mt: "3px", flexShrink: 0 }}
        />
        <Typography
          sx={{
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            color: "#334155",
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
            {item.title}
          </Box>
          {": "}
          {item.showLockIcon ? (
            <>
              {item.textBeforeIcon}{" "}
              <LockOpenOutlinedIcon
                sx={{
                  fontSize: 15,
                  color: "#d97706",
                  verticalAlign: "text-bottom",
                  mx: 0.25,
                }}
              />{" "}
              {item.textAfterIcon}
            </>
          ) : (
            item.text
          )}
        </Typography>
      </Box>
    ))}
  </Box>
  );
};

const CloudAccountsTable = ({
  accounts = [],
  isAzureView = false,
  roleAccess,
  licensing,
  numAccounts,
  orgLicensing,
  onPairAccount,
  onEditAccount,
  onDeleteAccount,
}) => {
  const userRole = getUserRole();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("accountNumber");
  const [order, setOrder] = useState("asc");
  const [useDefaultMasterSort, setUseDefaultMasterSort] = useState(true);

  const pairLabel = isAzureView ? "Pair Subscription" : "Pair Account";
  const accountNumberLabel = isAzureView ? "Subscription ID" : "Account Number";
  const isAwsMarketplaceLicense =
    orgLicensing?.data?.licenseName === "AwsMarketPlaceLicense";
  const pairedCount = (accounts || []).filter(
    (x) =>
      !x.accountNumber.includes("INIT00000001") &&
      !x.accountNumber.includes("TEST") &&
      x.accountNumber.length === 12
  ).length;
  const pairDisabled =
    isAwsMarketplaceLicense ||
    roleAccess < 0 ||
    pairedCount >= numAccounts ||
    !ALLOWED_ROLES.includes(userRole) ||
    orgLicensing?.data?.licenseName === "CUSTOMER";
  const pairTooltip = isAwsMarketplaceLicense
    ? "Please reach out to the CloudCatcher Support team for further assistance."
    : "";

  const decorated = useMemo(
    () =>
      (accounts || []).map((acct) => {
        const status = getAccountDisplayStatus(acct);
        return {
          acct,
          status,
          accountNumber: acct.accountNumber || "",
          accountName: acct.accountName || "",
          payerId: toTrimmedString(acct.payerAccountId),
          payerName: toTrimmedString(acct.payerAccountName),
          resourceGroup: getResourceGroupName(acct),
          cloudType: getAccountCloudType(acct),
          userCount: getUserCount(acct),
          addonCount: getAddonCount(acct),
          addedValue: getDateValue(
            acct.subscription_modified_date
              ? acct.subscription_modified_date
              : acct.createdDate
          ),
          addedLabel: formatDisplayDate(
            acct.subscription_modified_date
              ? acct.subscription_modified_date
              : acct.createdDate
          ),
          updatedValue: getDateValue(acct.lastModifiedDate),
          updatedLabel: formatDisplayDate(acct.lastModifiedDate),
        };
      }),
    [accounts]
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const rows = !term
      ? decorated
      : decorated.filter((row) => {
          const haystack = [
            row.accountNumber,
            row.accountName,
            isAzureView ? "" : row.payerId,
            isAzureView ? "" : row.payerName,
            row.resourceGroup,
            row.status.label,
            row.cloudType,
            row.acct.is_master ? "management" : "",
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });

    const sorted = [...rows].sort((a, b) => {
      if (useDefaultMasterSort) {
        if (a.acct.is_master && !b.acct.is_master) return -1;
        if (!a.acct.is_master && b.acct.is_master) return 1;
      }
      let cmp = 0;
      switch (orderBy) {
        case "cloudType":
          cmp = a.cloudType.localeCompare(b.cloudType);
          break;
        case "accountName":
          cmp = a.accountName.localeCompare(b.accountName);
          break;
        case "payer":
          cmp = (a.payerId || a.payerName).localeCompare(b.payerId || b.payerName);
          break;
        case "status":
          cmp = a.status.label.localeCompare(b.status.label);
          break;
        case "resourceGroup":
          cmp = a.resourceGroup.localeCompare(b.resourceGroup);
          break;
        case "added":
          cmp = a.addedValue - b.addedValue;
          break;
        case "updated":
          cmp = a.updatedValue - b.updatedValue;
          break;
        case "accountNumber":
        default:
          cmp = a.accountNumber.localeCompare(b.accountNumber);
          break;
      }
      return order === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [decorated, searchTerm, orderBy, order, useDefaultMasterSort, isAzureView]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage) || 1);

  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);

  const pagedRows = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (field) => {
    setUseDefaultMasterSort(false);
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const canManage =
    roleAccess >= 0 &&
    ALLOWED_ROLES.includes(userRole) &&
    licensing?.licenseName !==
      process.env.REACT_APP_FREE_ORGANIZATION_LICENSE;

  const SortableHeader = ({ field, children, align = "center" }) => (
    <TableCell
      align={align}
      sx={{
        ...HEADER_CELL_SX,
        textAlign: align,
        ...(align === "left" ? { pl: 2 } : null),
      }}
    >
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : "asc"}
        hideSortIcon={orderBy !== field}
        onClick={() => handleSort(field)}
        sx={{
          color: "inherit !important",
          display: "inline-flex",
          justifyContent: align === "left" ? "flex-start" : "center",
          alignItems: "center",
          maxWidth: "100%",
          whiteSpace: "nowrap",
          "& .MuiTableSortLabel-icon": {
            fontSize: 14,
            color: "#94a3b8",
            ml: 0.25,
          },
          "&.Mui-active": { color: "#64748b !important" },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1rem",
          }}
        >
          Cloud Accounts
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          Manage and review all connected cloud accounts for your organization.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: "1 1 160px", sm: "0 0 auto" },
              width: { xs: "auto", sm: 220 },
              minWidth: { xs: 0, sm: 220 },
              "& .MuiOutlinedInput-root": {
                height: 40,
                fontSize: "0.875rem",
                backgroundColor: "white",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": {
                  borderColor: "#111827",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                py: 0,
                height: "100%",
                boxSizing: "border-box",
              },
            }}
          />
          <AccountDetailTooltip title={pairTooltip}>
            <Button
              onClick={onPairAccount}
              disabled={pairDisabled}
              startIcon={<i className="mdi mdi-plus" />}
              sx={{
                height: 40,
                minHeight: 40,
                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                backgroundColor: "#F98125",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: "8px",
                px: 2,
                py: 0,
                boxShadow: "none",
                whiteSpace: "nowrap",
                lineHeight: 1,
                "& .MuiButton-startIcon": { mr: 0.75 },
                "&:hover": { backgroundColor: "#e8741c", boxShadow: "none" },
                "&.Mui-disabled": {
                  backgroundColor: "#e5e7eb",
                  color: "#9ca3af",
                },
              }}
            >
              <span id="AddAccountsBtn">{pairLabel}</span>
            </Button>
          </AccountDetailTooltip>
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          backgroundColor: "#fff",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Table
            size="small"
            sx={{
              width: "100%",
              minWidth: isAzureView ? 980 : 1100,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              {isAzureView ? (
                <>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "8%" }} />
                </>
              ) : (
                <>
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "8%" }} />
                </>
              )}
            </colgroup>
            <TableHead>
              <TableRow>
                <SortableHeader field="accountNumber" align="left">
                  {accountNumberLabel}
                </SortableHeader>
                <SortableHeader field="status">Status</SortableHeader>
                <SortableHeader field="accountName">Account Name</SortableHeader>
                {!isAzureView && (
                  <SortableHeader field="payer">Payer</SortableHeader>
                )}
                <SortableHeader field="resourceGroup">
                  Resource Group
                </SortableHeader>
                <TableCell align="center" sx={HEADER_CELL_SX}>
                  Users
                </TableCell>
                <TableCell align="center" sx={HEADER_CELL_SX}>
                  Add-ons
                </TableCell>
                <SortableHeader field="added">Added</SortableHeader>
                <SortableHeader field="updated">Last Updated</SortableHeader>
                <TableCell align="center" sx={HEADER_CELL_SX}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={isAzureView ? 9 : 10}
                    align="center"
                    sx={{ ...BODY_CELL_SX, py: 6 }}
                  >
                    <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>
                      {searchTerm
                        ? "No cloud accounts match your search."
                        : "No cloud accounts connected yet."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {pagedRows.map((row) => {
                const { acct, status } = row;
                const isExpired = acct.subscription_status === "expired";
                const isActive = acct.status === "ACTIVE";
                const editDisabled =
                  !canManage || isExpired || !isActive;
                const deleteDisabled =
                  roleAccess < 0 ||
                  !ALLOWED_ROLES.includes(userRole) ||
                  isExpired ||
                  !isActive;
                return (
                  <TableRow
                    key={acct.accountCode}
                    sx={{
                      "&:hover": { backgroundColor: "#f8fafc" },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell
                      align="left"
                      sx={{ ...BODY_CELL_SX, textAlign: "left", pl: 2 }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "flex-start",
                          gap: 1,
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        <CloudProviderMark
                          provider={row.cloudType}
                          sx={{ mr: 0, flexShrink: 0, mt: "1px" }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <TruncatedTooltipText
                            title={acct.accountNumber}
                            sx={{
                              ...CELL_TEXT,
                              fontVariantNumeric: "tabular-nums",
                              minWidth: 0,
                            }}
                          >
                            {acct.accountNumber}
                          </TruncatedTooltipText>
                          {acct.is_master && (
                            <Box
                              component="span"
                              sx={{
                                backgroundColor: "#2563eb",
                                color: "white",
                                fontSize: "0.625rem",
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                                padding: "1px 7px",
                                borderRadius: "999px",
                                lineHeight: 1.4,
                                textTransform: "lowercase",
                                whiteSpace: "nowrap",
                              }}
                            >
                              management
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      {(() => {
                        const statusChip = (
                          <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: status.color,
                              backgroundColor: status.backgroundColor,
                              borderRadius: "6px",
                              "& .MuiChip-label": { px: 1.25 },
                            }}
                          />
                        );
                        if (status.key !== "limited") return statusChip;
                        const inactiveModules = getInactiveStatusModules(acct);
                        if (!inactiveModules.length) return statusChip;
                        return (
                          <AccountDetailTooltip
                            title={`Inactive modules: ${inactiveModules.join(", ")}`}
                          >
                            {statusChip}
                          </AccountDetailTooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <TruncatedTooltipText
                        title={row.accountName}
                        sx={CELL_TEXT}
                      >
                        {row.accountName}
                      </TruncatedTooltipText>
                    </TableCell>
                    {!isAzureView && (
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <PayerCell
                          payerId={row.payerId}
                          payerName={row.payerName}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <TruncatedTooltipText
                        title={row.resourceGroup}
                        sx={CELL_TEXT}
                      >
                        {row.resourceGroup}
                      </TruncatedTooltipText>
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          ...CELL_TEXT,
                        }}
                      >
                        <PersonOutlineIcon
                          sx={{ fontSize: 16, color: "#64748b" }}
                        />
                        {row.userCount}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          ...CELL_TEXT,
                        }}
                      >
                        <ExtensionOutlinedIcon
                          sx={{ fontSize: 16, color: "#64748b" }}
                        />
                        {row.addonCount}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <Typography sx={CELL_TEXT}>{row.addedLabel}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <Typography sx={CELL_TEXT}>{row.updatedLabel}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={BODY_CELL_SX}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                        }}
                      >
                        {editDisabled ? (
                          <AccountDetailTooltip title="Edit Account">
                            <IconButton
                              size="small"
                              disabled
                              sx={ACTION_BTN_SX}
                            >
                              <i className="mdi mdi-pencil" style={{ fontSize: 15 }} />
                            </IconButton>
                          </AccountDetailTooltip>
                        ) : (
                          <AccountDetailTooltip title="Edit Account">
                            <IconButton
                              size="small"
                              onClick={() => onEditAccount(acct)}
                              sx={ACTION_BTN_SX}
                            >
                              <i className="mdi mdi-pencil" style={{ fontSize: 15 }} />
                            </IconButton>
                          </AccountDetailTooltip>
                        )}
                        {deleteDisabled ? (
                          <AccountDetailTooltip title="Delete Account">
                            <IconButton
                              size="small"
                              disabled
                              sx={DELETE_BTN_SX}
                            >
                              <i
                                className="mdi mdi-trash-can"
                                style={{ fontSize: 15 }}
                              />
                            </IconButton>
                          </AccountDetailTooltip>
                        ) : (
                          <AccountDetailTooltip title="Delete Account">
                            <IconButton
                              size="small"
                              onClick={() => onDeleteAccount(acct)}
                              sx={DELETE_BTN_SX}
                            >
                              <i
                                className="mdi mdi-trash-can"
                                style={{ fontSize: 15 }}
                              />
                            </IconButton>
                          </AccountDetailTooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <EditAccountTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={filtered.length}
          rowsPerPageOptions={[10, 20, 30]}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
        />
      </Box>
      <StatusLegend isAzureView={isAzureView} />
    </Box>
  );
};

export default CloudAccountsTable;
